import { Col, Divider, Row, Select, Tag } from "antd";
import { useTranslation } from "react-i18next";
import FieldBuilder from "../../form-components/field-builder";
import { languages } from "../../../utils/helpers/constants";
import { getDiscountStatus, getDiscountType, getDiscountTypeApp, getDiscountTypeValue, getTypeInvitationDiscountCode, getTypeUserDiscountCode } from "../../../models/discount-code/enum";
import { Controller, useWatch } from "react-hook-form";
import { useContext, useEffect, useState } from "react";
import { ITag } from "../../../models/tag/response";
import EndPoints from "../../../services/end-points";
import AppContext from "../../../context/app/context";
import FormItem from "../../general/form-item";
const Option = Select.Option;
const defaultUserTypeOptions = [4, 2, 3, 1];
const subscriptionCouponUserTypeOptions = [1, 3];

export const BasicInfoSection = ({ control, setValue }) => {
    const { t } = useTranslation();
    const { direction } = useContext(AppContext);

    const byInvitation = useWatch({
        control,
        name: "isInvitation",
    });

    const isSubscriptionCoupon = useWatch({
        control,
        name: "isSubscriptionCoupon",
    });

    const type = useWatch({
        control,
        name: "type",
    });

    const userType = useWatch({
        control,
        name: "userType",
    });

    const types = [1, 2];
    const status = [1, 2];
    const userTypeOptions = isSubscriptionCoupon ? subscriptionCouponUserTypeOptions : defaultUserTypeOptions;
    const [tags, setTags] = useState<ITag[]>([]);
    const [tagsLoading, setTagsLoading] = useState<boolean>(true);
    const [tagKeyword, setTagKeyword] = useState<string>();
    useEffect(() => {
        const getTags = async () => {
            try {
                const { data } = await EndPoints.tag.getAllTags({
                    light: true,
                    perPage: -1,
                });
                setTags(data.data);
            } catch (error) {
            } finally {
                setTagsLoading(false);
            }
        };
        getTags();
    }, [direction]);

    useEffect(() => {
        if (isSubscriptionCoupon) {
            setValue("typeValue", 2, { shouldDirty: true, shouldValidate: true });
        }
    }, [isSubscriptionCoupon, setValue]);

    useEffect(() => {
        if (isSubscriptionCoupon && !subscriptionCouponUserTypeOptions.includes(Number(userType))) {
            setValue("userType", 1, { shouldDirty: true, shouldValidate: true });
        }
    }, [isSubscriptionCoupon, userType, setValue]);

    return (
        <>
            <Divider orientation="center">{t("basic_info")}</Divider>
            <Row gutter={[16, 8]}>
                <Col span={24}>
                    <FieldBuilder
                        label={`${t("by_invitation")}`}
                        name="isInvitation"
                        input={{ type: "checkBox" }}
                    />
                </Col>
                <Col span={24}>
                    <FieldBuilder
                        label={`${t("is_subscription_coupon")}`}
                        name="isSubscriptionCoupon"
                        input={{ type: "checkBox" }}
                    />
                </Col>

                {byInvitation && (
                    <>
                        <Col span={24}>
                            <FieldBuilder
                                name="invitationType"
                                label={`${t("dedicated_to")}`}
                                width="large"
                                rules={{ required: true }}
                                input={{
                                    type: "radio",
                                    options: [1, 2]?.map((state, index) => ({
                                        key: index,
                                        label: t(getTypeInvitationDiscountCode(state)),
                                        value: state,
                                    })),
                                }}
                            />
                        </Col>
                    </>
                )}
                {languages?.map((lang, index) => (
                    <Col span={12} key={index}>
                        <FieldBuilder
                            name={`name[${lang?.code}]`}
                            label={`${t("name")} (${lang?.name})`}
                            input={{ type: "text" }}
                            rules={{ required: true }}
                        />
                    </Col>
                ))}

                <Col xs={24} sm={12} md={8} lg={8} xl={8} xxl={8}>
                    <FieldBuilder
                        name={"code"}
                        label={t("code")}
                        input={{ type: "text" }}
                        rules={{ required: true }}
                    />
                </Col>

                <Col xs={24} sm={12} md={12} lg={12} xl={12} xxl={12}>
                    <FieldBuilder
                        name="status"
                        label={`${t("status_discount")}`}
                        width="large"
                        rules={{ required: true }}
                        input={{
                            type: "select",
                            loading: false,
                            options: status?.map((state, index) => ({
                                key: index,
                                label: t(getDiscountStatus(state)),
                                value: state,
                            })),
                        }}
                    />
                </Col>

                {!byInvitation && <Col xs={24} sm={12} md={8} lg={8} xl={8} xxl={8}>
                    <FieldBuilder
                        name="type"
                        label={`${t("type_discount")}`}
                        width="large"
                        rules={{ required: true }}
                        disabled={byInvitation}
                        input={{
                            type: "select",
                            loading: false,
                            options: types?.map((state, index) => ({
                                key: index,
                                label: t(getDiscountType(state)),
                                value: state,
                            })),
                        }}
                    />
                </Col>}
                {(!byInvitation || isSubscriptionCoupon) && <Col xs={24} sm={12} md={8} lg={8} xl={8} xxl={8}>
                    <FieldBuilder
                        name="typeValue"
                        label={`${t("type_of_discount_value")}`}
                        width="large"
                        rules={{ required: true }}
                        disabled={byInvitation || isSubscriptionCoupon}
                        input={{
                            type: "select",
                            loading: false,
                            options: [1, 2]?.map((state, index) => ({
                                key: index,
                                label: t(getDiscountTypeValue(state)),
                                value: state,
                            })),
                        }}
                    />
                </Col>}
                <Col xs={24} sm={12} md={8} lg={8} xl={8} xxl={8}>
                    <FieldBuilder
                        name="typeApp"
                        label={`${t("type_of_discount_application")}`}
                        width="large"
                        rules={{ required: true }}
                        input={{
                            type: "select",
                            loading: false,
                            options: [1, 2, 3]?.map((state, index) => ({
                                key: index,
                                label: t(getDiscountTypeApp(state)),
                                value: state,
                            })),
                        }}
                    />
                </Col>
                <Col xs={24} sm={12} md={8} lg={8} xl={8} xxl={8}>
                    <FieldBuilder
                        name="valueOfAllDiscountCodes"
                        label={t("value_of_coupon")}
                        input={{ type: "number" }}
                        rules={{ required: true }}
                    />
                </Col>

                <Col xs={24} sm={12} md={8} lg={8} xl={8} xxl={8}>
                    <FormItem label={t("tags")} required>
                        <Controller
                            name="tagIds"
                            control={control}
                            rules={{ required: true }}
                            render={({ field: { ...field } }) => (
                                <Select
                                    loading={tagsLoading}
                                    {...field}
                                    onSearch={(val) => setTagKeyword(val)}
                                    style={{ width: "100%" }}
                                    allowClear
                                    mode="multiple"
                                    placeholder={t("tags")}
                                    filterOption={false}
                                >
                                    {tags
                                        .filter((tr) =>
                                            tagKeyword ? tr?.name?.includes(tagKeyword) : true
                                        )
                                        .map((tr, index) => (
                                            <Option key={index} value={tr?.id}>
                                                <Tag color={tr?.color}>{tr.name}</Tag>
                                            </Option>
                                        ))}
                                </Select>
                            )}
                        />
                    </FormItem>
                </Col>
                {!byInvitation && type == 1 && <Col xs={24} sm={12} md={8} lg={8} xl={8} xxl={8}>
                    <FieldBuilder
                        name="userType"
                        label={`${t("user_type")}`}
                        width="large"
                        rules={{ required: true }}
                        disabled={byInvitation}
                        input={{
                            type: "select",
                            loading: false,
                            options: userTypeOptions?.map((state, index) => ({
                                key: index,
                                label: t(getTypeUserDiscountCode(state)),
                                value: state,
                            })),
                        }}
                    />
                </Col>}

                {!byInvitation && type == 1 && userType != 3 && userType != 4 && <Col xs={12} sm={12} md={4}>
                    <FieldBuilder
                        name="enableAgeRange"
                        label={t("enable_age_range")}
                        input={{ type: "checkBox" }}
                    />
                </Col>}
                {!byInvitation && type == 1 && userType != 3 && userType != 4 && <Col xs={12} sm={12} md={4}>
                    <FieldBuilder
                        label={`${t("enable_gender")}`}
                        name="enableGender"
                        disabled={byInvitation}
                        input={{ type: "checkBox" }}
                    />
                </Col>}
                {!byInvitation && type == 1 && userType != 3 && userType != 4 && <Col xs={12} sm={12} md={4}>
                    <FieldBuilder
                        label={`${t("enable_area")}`}
                        name="enableArea"
                        disabled={byInvitation}
                        input={{ type: "checkBox" }}
                    />
                </Col>}
                {!byInvitation && type == 1 && userType != 3 && userType != 4 && userType != 2 && <Col xs={12} sm={12} md={6}>
                    <FieldBuilder
                        label={`${t("enable_number_of_orders")}`}
                        name="enableNumberOfOrders"
                        disabled={byInvitation}
                        input={{ type: "checkBox" }}
                    />
                </Col>}
                {!byInvitation && type == 1 && userType != 3 && userType != 4 && userType != 2 && <Col xs={12} sm={12} md={6}>
                    <FieldBuilder
                        label={`${t("enable_days_since_last_completed_order")}`}
                        name="enableDaysSinceLastCompletedOrder"
                        disabled={byInvitation}
                        input={{ type: "checkBox" }}
                    />
                </Col>}
                {!byInvitation && type == 2 && <Col span={12}>
                    <FieldBuilder
                        label={`${t("show_in_app")}`}
                        name="showInApp"
                        disabled={byInvitation}
                        input={{ type: "checkBox" }}
                    />
                </Col>}
            </Row>
        </>
    );
};
